/**
 * Which outcome and assessment links were added or removed.
 *
 * @module components/portfolio/comparison/mapping-differences
 */

import type { Programme } from "@/types";
import { stableJson } from "@/utils/stable-json";

import type { MappingDifference, MappingEdge } from "./diff-types";

function mappingEdges(programme: Programme): Map<string, MappingEdge> {
  const edges = new Map<string, MappingEdge>();
  const plos = new Map((programme.plos ?? []).map((plo) => [plo.id, plo]));
  const mimlos = new Map<string, { label: string; module: string }>();
  (programme.modules ?? []).forEach((module) =>
    (module.mimlos ?? []).forEach((mimlo) =>
      mimlos.set(mimlo.id, { label: mimlo.text, module: `${module.code}: ${module.title}` }),
    ),
  );

  (programme.plos ?? []).forEach((plo) =>
    (plo.standardMappings ?? []).forEach((mapping) => {
      const sourceLabel = [mapping.criteria, mapping.thread].filter(Boolean).join(" / ");
      const key = `standard:${plo.id}:${stableJson(mapping)}`;
      edges.set(key, {
        id: key,
        relationship: "Award standard to programme outcome",
        sourceLabel: sourceLabel || mapping.indicatorId || "Award standard indicator",
        sourceContext: [mapping.standardId, mapping.indicatorId].filter(Boolean).join(" · "),
        targetLabel: `${plo.code ?? plo.id}: ${plo.text}`,
      });
    }),
  );

  Object.entries(programme.ploToMimlos ?? {}).forEach(([ploId, mimloIds]) =>
    mimloIds.forEach((mimloId) => {
      const plo = plos.get(ploId);
      const mimlo = mimlos.get(mimloId);
      const key = `plo-mimlo:${ploId}:${mimloId}`;
      edges.set(key, {
        id: key,
        relationship: "Programme outcome to module outcome",
        sourceLabel: plo ? `${plo.code ?? plo.id}: ${plo.text}` : ploId,
        targetLabel: mimlo?.label ?? mimloId,
        ...(mimlo?.module ? { context: mimlo.module } : {}),
      });
    }),
  );

  (programme.modules ?? []).forEach((module) =>
    (module.assessments ?? []).forEach((assessment) =>
      (assessment.mimloIds ?? []).forEach((mimloId) => {
        const mimlo = mimlos.get(mimloId);
        const key = `mimlo-assessment:${module.id}:${mimloId}:${assessment.id}`;
        edges.set(key, {
          id: key,
          relationship: "Module outcome to assessment",
          sourceLabel: mimlo?.label ?? mimloId,
          targetLabel: assessment.title ?? assessment.type,
          context: `${module.code}: ${module.title}`,
        });
      }),
    ),
  );
  return edges;
}

export function compareMappings(before: Programme, after: Programme): MappingDifference[] {
  const beforeEdges = mappingEdges(before);
  const afterEdges = mappingEdges(after);
  const differences: MappingDifference[] = [];
  beforeEdges.forEach((edge, id) => {
    if (!afterEdges.has(id)) {
      differences.push({ ...edge, status: "removed" });
    }
  });
  afterEdges.forEach((edge, id) => {
    if (!beforeEdges.has(id)) {
      differences.push({ ...edge, status: "added" });
    }
  });
  return differences.sort(
    (left, right) =>
      left.relationship.localeCompare(right.relationship) ||
      left.sourceLabel.localeCompare(right.sourceLabel),
  );
}
